/**
 * Multisig actions — propose, approve, execute, cancel, and status checks
 * against the eosio.msig system contract.
 */

import { Serialize } from '@proton/js';
import { rpc } from './rpc.js';
import { REQUIRED_APPROVALS } from './constants.js';
import { getSession } from './wallet.js';
import { log } from './logger.js';

/** ABI cache to avoid re-fetching. */
const abiCache = new Map();

/**
 * Fetch the ABI for a contract and build a serialization-ready Contract object.
 * Results are cached per account.
 */
async function getContract(account) {
  if (abiCache.has(account)) return abiCache.get(account);

  const { abi } = await rpc('/v1/chain/get_abi', { account_name: account });
  if (!abi) throw new Error(`No ABI found for contract "${account}"`);

  const types = Serialize.getTypesFromAbi(Serialize.createInitialTypes(), abi);
  const actions = new Map();
  for (const { name, type } of abi.actions) {
    actions.set(name, Serialize.getType(types, type));
  }

  const contract = { types, actions };
  abiCache.set(account, contract);
  return contract;
}

// ---------------------------------------------------------------------------
// Propose
// ---------------------------------------------------------------------------

/**
 * Propose a multisig transaction for any system contract action.
 *
 * @param {object}   opts
 * @param {string}   opts.contractCode  — Contract account (e.g. 'eosio')
 * @param {string}   opts.actionName    — Action name (e.g. 'rmvproducer')
 * @param {object}   opts.actionArgs    — Action arguments to serialize
 * @param {string}   opts.proposalName  — Name for the proposal (max 12 chars)
 * @param {{actor:string, permission:string}[]} opts.requested — Requested approvers
 * @param {number}   opts.expiryDays    — Days until the inner transaction expires
 * @returns {Promise<string>} transaction ID
 */
export async function proposeMsigAction({ contractCode, actionName, actionArgs, proposalName, requested, expiryDays }) {
  const session = getSession();
  if (!session) throw new Error('Wallet not connected');

  const proposer = session.auth.actor.toString();

  if (requested.length < REQUIRED_APPROVALS) {
    throw new Error(
      `Need at least ${REQUIRED_APPROVALS} BPs selected. Currently: ${requested.length}`
    );
  }

  log(`Proposing ${contractCode}::${actionName} msig "${proposalName}"...`);
  log(`Proposer: ${proposer}, Requested approvals: ${requested.length}`);

  // 1. Get chain info for the inner transaction header
  const info = await rpc('/v1/chain/get_info');
  const headBlock = await rpc('/v1/chain/get_block', {
    block_num_or_id: info.head_block_num,
  });

  // 2. Serialize the action data client-side
  const contract = await getContract(contractCode);
  const actionDataHex = Serialize.serializeActionData(contract, contractCode, actionName, actionArgs);
  log(`Serialized ${actionName} data: ${actionDataHex}`);

  // 3. Build the inner transaction
  const expiration = new Date(Date.now() + expiryDays * 86_400_000)
    .toISOString()
    .split('.')[0];

  const innerTrx = {
    expiration,
    ref_block_num: info.head_block_num & 0xffff,
    ref_block_prefix: headBlock.ref_block_prefix,
    max_net_usage_words: 0,
    max_cpu_usage_ms: 0,
    delay_sec: 0,
    context_free_actions: [],
    actions: [
      {
        account: contractCode,
        name: actionName,
        authorization: [{ actor: 'eosio.prods', permission: 'active' }],
        data: actionDataHex,
      },
    ],
    transaction_extensions: [],
  };

  // 4. Send the propose transaction
  const result = await session.transact(
    {
      actions: [
        {
          account: 'eosio.msig',
          name: 'propose',
          authorization: [session.auth],
          data: {
            proposer,
            proposal_name: proposalName,
            requested,
            trx: innerTrx,
          },
        },
      ],
    },
    { broadcast: true }
  );

  const txId = result.processed?.id || result.transaction_id || 'unknown';
  log(`Proposal submitted successfully! TX: ${txId}`, 'success');
  return txId;
}

// ---------------------------------------------------------------------------
// Approve
// ---------------------------------------------------------------------------

/**
 * Approve an existing multisig proposal.
 *
 * @param {string} proposer      — Account that created the proposal
 * @param {string} proposalName  — Name of the proposal
 * @returns {Promise<string>} transaction ID
 */
export async function approveProposal(proposer, proposalName) {
  const session = getSession();
  if (!session) throw new Error('Wallet not connected');

  const actor = session.auth.actor.toString();
  log(`Approving proposal "${proposalName}" by ${proposer} as ${actor}...`);

  const result = await session.transact(
    {
      actions: [
        {
          account: 'eosio.msig',
          name: 'approve',
          authorization: [session.auth],
          data: {
            proposer,
            proposal_name: proposalName,
            level: {
              actor,
              permission: session.auth.permission.toString(),
            },
          },
        },
      ],
    },
    { broadcast: true }
  );

  const txId = result.processed?.id || result.transaction_id || 'unknown';
  log(`Approved! TX: ${txId}`, 'success');
  return txId;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

/**
 * Execute a multisig proposal (requires sufficient approvals).
 *
 * @param {string} proposer      — Account that created the proposal
 * @param {string} proposalName  — Name of the proposal
 * @returns {Promise<string>} transaction ID
 */
export async function executeProposal(proposer, proposalName) {
  const session = getSession();
  if (!session) throw new Error('Wallet not connected');

  const actor = session.auth.actor.toString();
  log(`Executing proposal "${proposalName}" by ${proposer}...`);

  const result = await session.transact(
    {
      actions: [
        {
          account: 'eosio.msig',
          name: 'exec',
          authorization: [session.auth],
          data: {
            proposer,
            proposal_name: proposalName,
            executer: actor,
          },
        },
      ],
    },
    { broadcast: true }
  );

  const txId = result.processed?.id || result.transaction_id || 'unknown';
  log(`EXECUTED SUCCESSFULLY! TX: ${txId}`, 'success');
  return txId;
}

// ---------------------------------------------------------------------------
// Cancel
// ---------------------------------------------------------------------------

/**
 * Cancel an existing multisig proposal (only the proposer can cancel).
 *
 * @param {string} proposalName — Name of the proposal
 * @returns {Promise<string>} transaction ID
 */
export async function cancelProposal(proposalName) {
  const session = getSession();
  if (!session) throw new Error('Wallet not connected');

  const actor = session.auth.actor.toString();
  log(`Cancelling proposal "${proposalName}"...`);

  const result = await session.transact(
    {
      actions: [
        {
          account: 'eosio.msig',
          name: 'cancel',
          authorization: [session.auth],
          data: {
            proposer: actor,
            proposal_name: proposalName,
            canceler: actor,
          },
        },
      ],
    },
    { broadcast: true }
  );

  const txId = result.processed?.id || result.transaction_id || 'unknown';
  log(`Proposal cancelled. TX: ${txId}`, 'success');
  return txId;
}

// ---------------------------------------------------------------------------
// Status / Query
// ---------------------------------------------------------------------------

/**
 * Check if a proposal exists and fetch its approval status.
 *
 * @param {string} proposer     — Account that created the proposal
 * @param {string} proposalName — Name of the proposal
 * @returns {Promise<{exists: boolean, requested: {actor:string, permission:string}[], approved: string[]}>}
 */
export async function getProposalStatus(proposer, proposalName) {
  log(`Checking for existing proposal "${proposalName}" by ${proposer}...`);

  // Check proposal table
  const proposals = await rpc('/v1/chain/get_table_rows', {
    code: 'eosio.msig',
    table: 'proposal',
    scope: proposer,
    lower_bound: proposalName,
    upper_bound: proposalName,
    limit: 1,
    json: true,
  });

  if (proposals.rows.length === 0) {
    log(`No proposal named "${proposalName}" found for ${proposer}.`, 'warn');
    return { exists: false, requested: [], approved: [] };
  }

  log(`Found existing proposal "${proposalName}" by ${proposer}.`, 'success');

  // Fetch approvals
  const approvals = await rpc('/v1/chain/get_table_rows', {
    code: 'eosio.msig',
    table: 'approvals2',
    scope: proposer,
    lower_bound: proposalName,
    upper_bound: proposalName,
    limit: 1,
    json: true,
  });

  if (approvals.rows.length === 0) {
    return { exists: true, requested: [], approved: [] };
  }

  const row = approvals.rows[0];
  const requestedApprovals = row.requested_approvals || [];
  const providedApprovals = row.provided_approvals || [];

  const allRequested = [
    ...requestedApprovals.map((r) => ({ actor: r.level.actor, permission: r.level.permission })),
    ...providedApprovals.map((r) => ({ actor: r.level.actor, permission: r.level.permission })),
  ];

  const approvedActors = providedApprovals.map((p) => p.level.actor);

  log(
    `Approvals: ${approvedActors.length} / ${REQUIRED_APPROVALS} required`,
    approvedActors.length >= REQUIRED_APPROVALS ? 'success' : 'info'
  );

  return { exists: true, requested: allRequested, approved: approvedActors };
}
